import * as React from 'react';

export type PickFile = {
  name: string;
  dataURI: string;
};

type Props = {
  children: (args: { pick: () => void; file?: PickFile; onClear: () => void }) => React.ReactNode;
  acceptedFileTypes?: string;
};

type State = {
  file?: PickFile;
};

/* A file picker, to use with the the file uploader:
 *
 * <Picker>
 *    {({ pick, file }) => {
 *      if (!file) {
 *        return <button onClick={pick}>choose a file</button>;
 *      }
 *
 *      return (
 *        <Uploader file={file}>
 *          {({ progress, url }) => (
 *            <span>
 *              uploading: {progress}% {url}
 *            </span>
 *          )}
 *        </Uploader>
 *      );
 *    }}
 * </Picker>
 *
 */
class Picker extends React.Component<Props, State> {
  state: State = {};

  constructor(props: Props) {
    super(props);
    this.input = React.createRef();
  }

  pick = () => {
    if (this.input && this.input.current) {
      this.input.current.click();
    }
  };

  // TS doesn't have good support for `InputEvent`s yet
  // so it complains about `files` not existing on `EventTarget`
  handleFilePicked = (event: any) => {
    const file: File = event.target.files[0];
    const fileReader = new FileReader();

    fileReader.onload = (onLoadEvent: any) => {
      this.setState({
        file: {
          // TS also doesn't have good support for `FileReaderEvent`s
          // so it complains about `result` not existing on `EventTarget`
          dataURI: onLoadEvent.target ? onLoadEvent.target.result : '',
          name: file.name,
        },
      });
    };

    fileReader.readAsDataURL(file);
  };

  handleClearFile = () => this.setState({ file: undefined });

  input: React.RefObject<HTMLInputElement>;

  render() {
    return [
      <input
        key="input"
        type="file"
        accept={this.props.acceptedFileTypes || '*'}
        ref={this.input}
        style={{ display: 'none' }}
        onChange={this.handleFilePicked}
      />,
      <React.Fragment key="picker_child">
        {this.props.children({
          pick: this.pick,
          file: this.state.file,
          onClear: this.handleClearFile,
        })}
      </React.Fragment>,
    ];
  }
}

export default Picker;
